import React, {
  useEffect,
  useMemo,
  useState,
  useRef,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  collection,
  doc,
  runTransaction,
  onSnapshot,
  query,
  updateDoc,
  where,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase";
import websocket from "../services/websocket";
import ForexBottomNav from "../Components/ForexBottomNav";
import "./Trader.css";
import ForexHeader from "./ForexHeader";
const Trades = () => {
  const navigate = useNavigate();

  // CURRENT USER
  const username = localStorage.getItem("username")?.trim() || "";

  // STATE
  const [realBalance, setRealBalance] = useState(null);
  const [userDocId, setUserDocId] = useState(null);
  const stopOutRunningRef = useRef(false);
  const stopOutClosedRef = useRef(new Set());
  const STOP_OUT_LEVEL = 50;

  const [activeTab, setActiveTab] = useState("open");
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [prices, setPrices] = useState({});
  const [expandedTrade, setExpandedTrade] = useState(null);
  const [closingId, setClosingId] = useState(null);

  // CLOSED FILTER
  const [filterOpen, setFilterOpen] = useState(false);
  const [closedFilter, setClosedFilter] = useState("24h");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [customOpen, setCustomOpen] = useState(false);

  // LISTEN TO USER BALANCE
  useEffect(() => {
    if (!username) {
      setRealBalance(null);
      setUserDocId(null);
      return undefined;
    }

    const usersQuery = query(
      collection(db, "users"),
      where("username", "==", username)
    );

    const unsubscribe = onSnapshot(
      usersQuery,
      (snapshot) => {
        if (snapshot.empty) {
          setRealBalance(null);
          setUserDocId(null);
          console.error("User document haijapatikana.");
          return;
        }

        const userDoc = snapshot.docs[0];
        const userData = userDoc.data();
        const balance = Number(userData.usdtBalanceOther);

        setUserDocId(userDoc.id);
        setRealBalance(
          Number.isFinite(balance) ? balance : null
        );
      },
      (error) => {
        console.error("Failed to read real balance:", error);
        setRealBalance(null);
      }
    );

    return () => unsubscribe();
  }, [username]);

  // FIRESTORE FOREX ORDERS
  useEffect(() => {
    if (!username) {
      setTrades([]);
      setLoading(false);
      return undefined;
    }

    setLoading(true);

    const forexQuery = query(
      collection(db, "Forex"),
      where("username", "==", username)
    );

    const unsubscribe = onSnapshot(
      forexQuery,
      (snapshot) => {
        const orders = snapshot.docs.map((item) => ({
          id: item.id,
          ...item.data(),
        }));

        orders.sort((a, b) => {
          const aTime = a.createdAt?.toMillis?.() || 0;
          const bTime = b.createdAt?.toMillis?.() || 0;
          return bTime - aTime;
        });

        setTrades(orders);
        setLoading(false);
      },
      (error) => {
        console.error("Failed to load Forex trades:", error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [username]);

  // SYMBOLS REQUIRING LIVE PRICE
  const liveSymbols = useMemo(() => {
    const symbols = trades
      .filter(
        (trade) =>
          !trade.closed &&
          (trade.status === "open" ||
            trade.status === "pending")
      )
      .map((trade) =>
        String(trade.symbol || "")
          .replace(/[^A-Za-z]/g, "")
          .toUpperCase()
      )
      .filter(Boolean);

    return [...new Set(symbols)];
  }, [trades]);

  // LIVE FOREX PRICE
  useEffect(() => {
    if (liveSymbols.length === 0) {
      return undefined;
    }

    websocket.connect();

    const unsubscribe = websocket.onTick((data) => {
      if (data?.tick) {
        const apiSymbol = String(
          data.tick.symbol ||
            data.tick.underlying_symbol ||
            ""
        );

        const price = Number(data.tick.quote);

        if (
          !Number.isFinite(price) ||
          price <= 0 ||
          !apiSymbol.startsWith("frx")
        ) {
          return;
        }

        const clean = apiSymbol
          .replace(/^frx/i, "")
          .toUpperCase();

        setPrices((previous) => ({
          ...previous,
          [clean]: price,
        }));

        return;
      }

      if (data?.ohlc) {
        const apiSymbol = String(
          data.ohlc.symbol ||
            data.ohlc.underlying_symbol ||
            ""
        );

        const price = Number(data.ohlc.close);

        if (
          !Number.isFinite(price) ||
          price <= 0 ||
          !apiSymbol.startsWith("frx")
        ) {
          return;
        }

        const clean = apiSymbol
          .replace(/^frx/i, "")
          .toUpperCase();

        setPrices((previous) => ({
          ...previous,
          [clean]: price,
        }));
      }
    });

    liveSymbols.forEach((symbol) => {
      websocket.history(`frx${symbol}`, 30);
    });

    return () => {
      if (typeof unsubscribe === "function") {
        unsubscribe();
      }
    };
  }, [liveSymbols]);

  // CALCULATE LIVE P&L
  const calculateProfitLoss = (trade) => {
    if (trade.closed || trade.status === "closed") {
      return Number(
        trade.realisedProfitLoss ??
          trade.profitLoss ??
          0
      );
    }

    const symbol = String(trade.symbol || "")
      .replace(/[^A-Za-z]/g, "")
      .toUpperCase();

    const currentPrice = Number(
      prices[symbol] ??
        trade.currentPrice ??
        trade.marketPrice ??
        trade.entryPrice ??
        0
    );

    const entryPrice = Number(
      trade.openPrice ??
        trade.entryPrice ??
        0
    );

    const units = Number(
      trade.units ||
        Number(trade.lots || 0) * 100000
    );

    if (!currentPrice || !entryPrice || !units) {
      return 0;
    }

    const side = String(trade.side || "").toLowerCase();

    const difference =
      side === "sell"
        ? entryPrice - currentPrice
        : currentPrice - entryPrice;

    return difference * units;
  };

  // OPEN ORDERS
  const openTrades = useMemo(
    () =>
      trades.filter(
        (trade) =>
          trade.status === "open" &&
          !trade.closed
      ),
    [trades]
  );

  // PENDING ORDERS
  const pendingTrades = useMemo(
    () =>
      trades.filter(
        (trade) =>
          trade.status === "pending" &&
          !trade.closed
      ),
    [trades]
  );

  // =====================================================
  // AUTO STOP OUT — 50% MARGIN LEVEL
  // =====================================================
  useEffect(() => {
    if (
      realBalance === null ||
      !Number.isFinite(realBalance) ||
      openTrades.length === 0 ||
      stopOutRunningRef.current
    ) {
      return;
    }

    const runStopOut = async () => {
      if (stopOutRunningRef.current) return;

      stopOutRunningRef.current = true;

      try {
        const candidates = openTrades
          .filter(
            (trade) =>
              trade.status === "open" &&
              !trade.closed &&
              !stopOutClosedRef.current.has(trade.id)
          )
          .map((trade) => ({
            trade,
            pnl: calculateProfitLoss(trade),
            margin: Number(trade.marginRequired) || 0,
          }));

        const usedMargin = candidates.reduce(
          (sum, item) => sum + item.margin,
          0
        );

        if (usedMargin <= 0 || candidates.length === 0) {
          return;
        }

        const floatingPnL = candidates.reduce(
          (sum, item) => sum + item.pnl,
          0
        );

        const equity = realBalance + floatingPnL;
        const marginLevel = (equity / usedMargin) * 100;

        if (
          !Number.isFinite(marginLevel) ||
          marginLevel > STOP_OUT_LEVEL
        ) {
          return;
        }

        // Close the trade with the largest loss first.
        const worstTrade = [...candidates].sort(
          (a, b) => a.pnl - b.pnl
        )[0];

        if (!worstTrade) return;

        const trade = worstTrade.trade;
        const symbol = String(trade.symbol || "")
          .replace(/[^A-Za-z]/g, "")
          .toUpperCase();

        const closePrice = Number(
          prices[symbol] ??
            trade.currentPrice ??
            trade.marketPrice ??
            trade.openPrice ??
            trade.entryPrice
        );

        if (
          !Number.isFinite(closePrice) ||
          closePrice <= 0
        ) {
          console.warn(
            "Stop Out imesubiri: live close price haipatikani.",
            trade.symbol
          );
          return;
        }

        stopOutClosedRef.current.add(trade.id);

        // Calculate P/L using the exact close price.
        const entryPrice = Number(
          trade.openPrice ?? trade.entryPrice ?? 0
        );

        const units = Number(
          trade.units ||
            Number(trade.lots || 0) * 100000
        );

        const side = String(trade.side || "").toLowerCase();

        const pnlAtClose =
          !entryPrice || !units
            ? 0
            : (side === "sell"
                ? entryPrice - closePrice
                : closePrice - entryPrice) * units;

        try {
          await settleTrade({
            trade,
            closePrice,
            profitLoss: pnlAtClose,
            closeReason: "stop_out",
            marginLevel,
          });

          console.warn("AUTO STOP OUT:", {
            symbol: trade.symbol,
            pnl: pnlAtClose,
            marginLevel,
          });
        } catch (error) {
          stopOutClosedRef.current.delete(trade.id);
          throw error;
        }
      } catch (error) {
        console.error("Auto Stop Out failed:", error);
      } finally {
        stopOutRunningRef.current = false;
      }
    };

    runStopOut();
  }, [realBalance, openTrades, prices]);

  // =====================================================
  // SETTLE TRADE — UPDATE TRADE AND BALANCE
  // =====================================================
  const settleTrade = async ({
    trade,
    closePrice,
    profitLoss,
    closeReason = "manual",
    marginLevel = null,
  }) => {
    if (!userDocId) {
      throw new Error("User document haijapatikana.");
    }

    const tradeRef = doc(db, "Forex", trade.id);
    const userRef = doc(db, "users", userDocId);

    await runTransaction(db, async (transaction) => {
      const tradeSnapshot = await transaction.get(tradeRef);
      const userSnapshot = await transaction.get(userRef);

      if (!tradeSnapshot.exists()) {
        throw new Error("Trade haipo tena.");
      }

      if (!userSnapshot.exists()) {
        throw new Error("User document haipo tena.");
      }

      const currentTrade = tradeSnapshot.data();

      // Avoid settling a trade more than once.
      if (
        currentTrade.status === "closed" ||
        currentTrade.closed === true
      ) {
        return;
      }

      const userData = userSnapshot.data();
      const currentBalance = Number(userData.usdtBalanceOther);

      if (!Number.isFinite(currentBalance)) {
        throw new Error("Balance ya mtumiaji si sahihi.");
      }

      const nextBalance = currentBalance + Number(profitLoss || 0);

      transaction.update(tradeRef, {
        status: "closed",
        closed: true,
        closePrice,
        currentPrice: closePrice,
        profitLoss: Number(profitLoss || 0),
        realisedProfitLoss: Number(profitLoss || 0),
        closeReason,
        ...(marginLevel !== null
          ? { stopOutMarginLevel: marginLevel }
          : {}),
        closedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      transaction.update(userRef, {
        usdtBalanceOther: nextBalance,
        updatedAt: serverTimestamp(),
      });
    });
  };

  // =====================================================
  // AUTO EXECUTE PENDING ORDERS
  // =====================================================
  useEffect(() => {
    if (pendingTrades.length === 0) {
      return;
    }

    pendingTrades.forEach(async (trade) => {
      try {
        const symbol = String(trade.symbol || "")
          .replace(/[^A-Za-z]/g, "")
          .toUpperCase();

        const marketPrice = Number(prices[symbol]);

        if (
          !Number.isFinite(marketPrice) ||
          marketPrice <= 0
        ) {
          return;
        }

        const entryPrice = Number(
          trade.entryPrice ?? trade.openPrice ?? 0
        );

        if (!Number.isFinite(entryPrice)) {
          return;
        }

        const side = String(trade.side || "").toLowerCase();
        const type = String(trade.type || "").toLowerCase();

        let shouldExecute = false;

        if (
          side === "buy" &&
          type === "limit" &&
          marketPrice <= entryPrice
        ) {
          shouldExecute = true;
        }

        if (
          side === "buy" &&
          type === "stop" &&
          marketPrice >= entryPrice
        ) {
          shouldExecute = true;
        }

        if (
          side === "sell" &&
          type === "limit" &&
          marketPrice >= entryPrice
        ) {
          shouldExecute = true;
        }

        if (
          side === "sell" &&
          type === "stop" &&
          marketPrice <= entryPrice
        ) {
          shouldExecute = true;
        }

        if (!shouldExecute) {
          return;
        }

        await updateDoc(doc(db, "Forex", trade.id), {
          status: "open",
          closed: false,
          openPrice: marketPrice,
          currentPrice: marketPrice,
          marketPrice,
          openedAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });

        console.log(
          "Pending executed:",
          trade.symbol,
          marketPrice
        );
      } catch (error) {
        console.error("Pending execution failed:", error);
      }
    });
  }, [pendingTrades, prices]);

  // =====================================================
  // AUTO EXPIRE PENDING ORDERS
  // =====================================================
  useEffect(() => {
    const timer = setInterval(async () => {
      const now = Date.now();

      pendingTrades.forEach(async (trade) => {
        if (!trade.expiry) return;

        const expiry = new Date(trade.expiry).getTime();

        if (Number.isNaN(expiry) || expiry > now) {
          return;
        }

        try {
          await updateDoc(doc(db, "Forex", trade.id), {
            status: "expired",
            closed: true,
            expiredAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          });

          console.log("Order expired:", trade.symbol);
        } catch (error) {
          console.error(error);
        }
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [pendingTrades]);

  // CLOSED ORDERS
  const allClosedTrades = useMemo(
    () =>
      trades.filter(
        (trade) =>
          trade.status === "closed" ||
          trade.closed === true
      ),
    [trades]
  );

  // FILTER CLOSED TRADES
  const closedTrades = useMemo(() => {
    const now = Date.now();

    return allClosedTrades.filter((trade) => {
      const closedAt = trade.closedAt?.toMillis?.();

      if (!closedAt) return true;

      if (closedFilter === "24h") {
        return closedAt >= now - 24 * 60 * 60 * 1000;
      }

      if (closedFilter === "7d") {
        return closedAt >= now - 7 * 24 * 60 * 60 * 1000;
      }

      if (closedFilter === "30d") {
        return closedAt >= now - 30 * 24 * 60 * 60 * 1000;
      }

      if (
        closedFilter === "custom" &&
        customStart &&
        customEnd
      ) {
        const start = new Date(
          `${customStart}T00:00:00`
        ).getTime();

        const end = new Date(
          `${customEnd}T23:59:59`
        ).getTime();

        return closedAt >= start && closedAt <= end;
      }

      return true;
    });
  }, [
    allClosedTrades,
    closedFilter,
    customStart,
    customEnd,
  ]);

  // CURRENT TAB ORDERS
  const visibleTrades =
    activeTab === "open"
      ? openTrades
      : activeTab === "pending"
      ? pendingTrades
      : closedTrades;

  // TOTAL P&L
  const totalProfitLoss = visibleTrades.reduce(
    (total, trade) =>
      total + calculateProfitLoss(trade),
    0
  );

  // MONEY
  const formatMoney = (value) => {
    const number = Number(value) || 0;
    const sign = number < 0 ? "-" : "";
    return `${sign}$${Math.abs(number).toFixed(2)}`;
  };

  // PRICE
  const formatPrice = (value) => {
    const number = Number(value);

    if (!Number.isFinite(number)) {
      return "--";
    }

    return number.toFixed(5);
  };

  // CLOSE TRADE MANUALLY
  const closeTrade = async (trade) => {
    if (closingId || trade.status !== "open") {
      return;
    }

    try {
      setClosingId(trade.id);

      const symbol = String(trade.symbol || "")
        .replace(/[^A-Za-z]/g, "")
        .toUpperCase();

      const closePrice = Number(
        prices[symbol] ??
          trade.currentPrice ??
          trade.marketPrice
      );

      if (
        !Number.isFinite(closePrice) ||
        closePrice <= 0
      ) {
        throw new Error("Current market price unavailable");
      }

      const entryPrice = Number(
        trade.openPrice ?? trade.entryPrice ?? 0
      );

      const units = Number(
        trade.units ||
          Number(trade.lots || 0) * 100000
      );

      const side = String(trade.side || "").toLowerCase();

      const profitLoss =
        !entryPrice || !units
          ? 0
          : (side === "sell"
              ? entryPrice - closePrice
              : closePrice - entryPrice) * units;

      await settleTrade({
        trade,
        closePrice,
        profitLoss,
        closeReason: "manual",
      });
    } catch (error) {
      console.error("Failed to close trade:", error);

      alert(
        error?.message ||
          "Failed to close trade"
      );
    } finally {
      setClosingId(null);
    }
  };

  // CLOSED FILTER LABEL
  const closedFilterLabel =
    closedFilter === "24h"
      ? "Last 24 hours"
      : closedFilter === "7d"
      ? "Last 7 days"
      : closedFilter === "30d"
      ? "Last 30 days"
      : "Custom";

  // SELECT FILTER
  const selectFilter = (value) => {
    if (value === "custom") {
      setClosedFilter("custom");
      setFilterOpen(false);
      setCustomOpen(true);
      return;
    }

    setClosedFilter(value);
    setFilterOpen(false);
  };

  // =====================================================
  // RETURN UI
  // =====================================================
  return (
    <div className="trades-page">
     <ForexHeader/>
      <ForexBottomNav />

      <header className="trades-header">
        <h1>Trades</h1>
      </header>

      <div className="trades-tabs">
        <button
          type="button"
          className={activeTab === "open" ? "active" : ""}
          onClick={() => setActiveTab("open")}
        >
          <span>Open</span>
          {openTrades.length > 0 && (
            <strong className="trade-count">
              {openTrades.length}
            </strong>
          )}
        </button>

        <button
          type="button"
          className={activeTab === "pending" ? "active" : ""}
          onClick={() => setActiveTab("pending")}
        >
          <span>Pending</span>
          {pendingTrades.length > 0 && (
            <strong className="trade-count">
              {pendingTrades.length}
            </strong>
          )}
        </button>

        <button
          type="button"
          className={activeTab === "closed" ? "active" : ""}
          onClick={() => setActiveTab("closed")}
        >
          Closed
        </button>
      </div>

      <main className="trades-content">
        <div className="trades-summary">
          <div className="trades-total">
            <span>Total:</span>
            <strong
              className={
                totalProfitLoss > 0
                  ? "profit"
                  : totalProfitLoss < 0
                  ? "loss"
                  : ""
              }
            >
              {formatMoney(totalProfitLoss)}
            </strong>
            <span className="summary-arrow">⌄</span>
          </div>

          {activeTab === "closed" && (
            <button
              type="button"
              className="closed-filter-button"
              onClick={() => setFilterOpen(true)}
            >
              <span>{closedFilterLabel}</span>
              <span>⌄</span>
            </button>
          )}
        </div>

        {loading && (
          <div className="trades-loading">
            Loading trades...
          </div>
        )}

        {!loading && visibleTrades.length === 0 && (
          <div className="no-trades">
            <div className="no-trades-icon">↕️</div>
            <h3>No {activeTab} trades</h3>
            <p>
              Your {activeTab} Forex trades will appear here.
            </p>
          </div>
        )}

        {!loading &&
          visibleTrades.map((trade) => {
            const pnl = calculateProfitLoss(trade);
            const isClosed =
              trade.closed === true ||
              trade.status === "closed";

            const isPending = trade.status === "pending";
            const side = String(trade.side || "").toLowerCase();

            const symbol = String(
              trade.symbol ||
                trade.displaySymbol ||
                ""
            ).toUpperCase();

            const cleanSymbol = String(trade.symbol || "")
              .replace(/[^A-Za-z]/g, "")
              .toUpperCase();

            const entryPrice = Number(
              trade.openPrice ??
                trade.entryPrice ??
                0
            );

            const isExpanded = expandedTrade === trade.id;

            return (
              <article
                className="trade-card"
                key={trade.id}
              >
                <div className="trade-card-top">
                  {!isClosed && (
                    <button
                      type="button"
                      className="trade-expand"
                      onClick={() =>
                        setExpandedTrade(
                          isExpanded ? null : trade.id
                        )
                      }
                    >
                      {isExpanded ? "⌃" : "⌄"}
                    </button>
                  )}

                  <span
                    className={
                      side === "sell"
                        ? "trade-side sell"
                        : "trade-side buy"
                    }
                  >
                    {isPending
                      ? `${side === "buy" ? "Buy" : "Sell"} ${
                          trade.type === "stop"
                            ? "Stop"
                            : "Limit"
                        }`
                      : side === "buy"
                      ? "Buy"
                      : "Sell"}
                  </span>

                  <button
                    type="button"
                    className="trade-symbol"
                  >
                    <strong>{symbol}</strong>
                    <span>›</span>
                  </button>

                  <strong
                    className={
                      pnl > 0
                        ? "trade-pnl profit"
                        : pnl < 0
                        ? "trade-pnl loss"
                        : "trade-pnl"
                    }
                  >
                    {formatMoney(pnl)}
                  </strong>
                </div>

                <div className="trade-card-bottom">
                  <div className="trade-entry">
                    <strong>
                      {Number(trade.lots || 0).toFixed(2)} lots
                    </strong>
                    <span>@ {formatPrice(entryPrice)}</span>
                  </div>

                  {isPending ? (
                    <div className="trade-actions">
                      <button
                        type="button"
                        className="trade-edit"
                        onClick={() =>
                          navigate(`/trade/${trade.id}`)
                        }
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="trade-cancel"
                        onClick={async () => {
                          try {
                            await updateDoc(
                              doc(db, "Forex", trade.id),
                              {
                                status: "cancelled",
                                closed: true,
                                updatedAt: serverTimestamp(),
                              }
                            );
                          } catch (error) {
                            console.error(error);
                            alert("Imeshindikana kufuta pending order.");
                          }
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  ) : isClosed ? (
                    <div className="closed-info">
                      <span>Closed @ </span>
                      <strong>
                        {formatPrice(trade.closePrice)}
                      </strong>
                    </div>
                  ) : (
                    <div className="trade-actions">
                      <button
                        type="button"
                        className="trade-more"
                        onClick={() =>
                          navigate(`/trade/${trade.id}`)
                        }
                      >
                        More
                      </button>

                      <button
                        type="button"
                        className="trade-close"
                        disabled={closingId === trade.id}
                        onClick={() => closeTrade(trade)}
                      >
                        {closingId === trade.id
                          ? "Closing..."
                          : "Close"}
                      </button>
                    </div>
                  )}
                </div>

                {isExpanded && (
                  isPending ? (
                    <div className="trade-details">
                      <div>
                        <span>Trade ID</span>
                        <strong>{trade.tradeId || trade.id}</strong>
                      </div>
                      <div>
                        <span>Current price</span>
                        <strong>
                          {formatPrice(prices[cleanSymbol])}
                        </strong>
                      </div>
                      <div>
                        <span>Stop loss</span>
                        <strong>{formatPrice(trade.stopLoss)}</strong>
                      </div>
                      <div>
                        <span>Take profit</span>
                        <strong>{formatPrice(trade.takeProfit)}</strong>
                      </div>
                      <div>
                        <span>Expiration</span>
                        <strong>{trade.expiry || "--"}</strong>
                      </div>
                    </div>
                  ) : (
                    <div className="trade-details">
                      <div>
                        <span>Market price</span>
                        <strong>
                          {formatPrice(
                            prices[cleanSymbol] ??
                              trade.currentPrice
                          )}
                        </strong>
                      </div>

                      <div>
                        <span>Units</span>
                        <strong>
                          {Number(
                            trade.units || 0
                          ).toLocaleString("en-GB")}
                        </strong>
                      </div>

                      <div>
                        <span>Margin</span>
                        <strong>
                          $
                          {Number(
                            trade.marginRequired || 0
                          ).toFixed(2)}
                        </strong>
                      </div>

                      {trade.stopLossEnabled && (
                        <div>
                          <span>Stop loss</span>
                          <strong>
                            {formatPrice(trade.stopLoss)}
                          </strong>
                        </div>
                      )}

                      {trade.takeProfitEnabled && (
                        <div>
                          <span>Take profit</span>
                          <strong>
                            {formatPrice(trade.takeProfit)}
                          </strong>
                        </div>
                      )}

                      <div>
                        <span>Floating P/L</span>
                        <strong>{formatMoney(pnl)}</strong>
                      </div>
                    </div>
                  )
                )}
              </article>
            );
          })}
      </main>

      {/* FILTER BOTTOM SHEET */}
      {filterOpen && (
        <div
          className="trade-modal-overlay"
          onClick={() => setFilterOpen(false)}
        >
          <div
            className="trade-filter-sheet"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="filter-options">
              <button
                type="button"
                onClick={() => selectFilter("24h")}
              >
                Last 24 hours
              </button>
              <button
                type="button"
                onClick={() => selectFilter("7d")}
              >
                Last 7 days
              </button>
              <button
                type="button"
                onClick={() => selectFilter("30d")}
              >
                Last 30 days
              </button>
              <button
                type="button"
                onClick={() => selectFilter("custom")}
              >
                Custom
              </button>
            </div>

            <button
              type="button"
              className="filter-cancel"
              onClick={() => setFilterOpen(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* CUSTOM DATE */}
      {customOpen && (
        <div
          className="trade-modal-overlay"
          onClick={() => setCustomOpen(false)}
        >
          <div
            className="custom-date-sheet"
            onClick={(event) => event.stopPropagation()}
          >
            <h3>Custom period</h3>

            <label>
              From
              <input
                type="date"
                value={customStart}
                onChange={(event) =>
                  setCustomStart(event.target.value)
                }
              />
            </label>

            <label>
              To
              <input
                type="date"
                value={customEnd}
                onChange={(event) =>
                  setCustomEnd(event.target.value)
                }
              />
            </label>

            <button
              type="button"
              className="custom-apply"
              disabled={
                !customStart ||
                !customEnd ||
                customStart > customEnd
              }
              onClick={() => setCustomOpen(false)}
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Trades;